import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  getJobProfileSettings,
  saveJobProfileSettings,
} from '@/api/jobProfileSettings';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  normalizeFieldRows,
  normalizeLevelRows,
  validateForSave,
  withOrderIndex,
} from '@/helpers/jobProfileSettingsValidation';

export const useJobProfileSettings = defineStore('jobProfileSettings', () => {
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const fields = ref([]);
  const levels = ref([]);
  const isDirty = ref(false);

  const validationErrors = ref([]);

  const isSaveValid = computed(() => {
    const { ok } = validateForSave({
      fields: fields.value,
      levels: levels.value,
    });

    return ok;
  });

  const syncSortOrders = () => {
    fields.value = withOrderIndex(normalizeFieldRows(fields.value));
    levels.value = withOrderIndex(normalizeLevelRows(levels.value));
    isDirty.value = true;
  };

  const hydrateFromPayload = (payload) => {
    const nextFields = normalizeFieldRows(payload.fields || []);
    const nextLevels = normalizeLevelRows(payload.levels || []);

    fields.value = withOrderIndex(nextFields);
    levels.value = withOrderIndex(nextLevels);

    validationErrors.value = [];
    isDirty.value = false;
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });

      const data = await getJobProfileSettings();

      hydrateFromPayload(data);
    } catch (error) {
      const message = error?.message || String(error);

      addErrorSnackbar({ text: `Job profile settings - ${message}` });

      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const save = async () => {
    const { ok, errors } = validateForSave({
      fields: fields.value,
      levels: levels.value,
    });

    validationErrors.value = errors;

    if (!ok) {
      addErrorSnackbar({ text: errors[0] || 'Invalid settings' });

      return;
    }

    syncSortOrders();

    try {
      setLoading({ componentLoading: true });

      await saveJobProfileSettings({
        fields: fields.value,
        levels: levels.value,
      });

      isDirty.value = false;
      addSuccessSnackbar({ text: 'Job profile settings have been updated' });
    } catch (error) {
      const message = error?.message || String(error);

      addErrorSnackbar({ text: `Job profile settings - ${message}` });

      throw error;
    } finally {
      setLoading({ componentLoading: false });
    }
  };

  const setFieldVisible = (fieldUlid, visible) => {
    fields.value = fields.value.map((row) => {
      if (row.ulid !== fieldUlid) return row;

      const nextHidden = !visible;
      const nextRequired = nextHidden ? false : row.is_required;

      return { ...row, is_hidden: nextHidden, is_required: nextRequired };
    });

    isDirty.value = true;
    syncSortOrders();
  };

  const setFieldRequired = (fieldUlid, required) => {
    fields.value = fields.value.map((row) => {
      if (row.ulid !== fieldUlid) return row;

      return {
        ...row,
        is_required: Boolean(required) && !row.is_hidden,
      };
    });
    isDirty.value = true;
    syncSortOrders();
  };

  const setLevelAlias = (levelUlid, shortName) => {
    levels.value = levels.value.map((row) => (
      row.ulid === levelUlid ? { ...row, short_name: String(shortName ?? '') } : row
    ));

    isDirty.value = true;
    syncSortOrders();
  };

  return {
    fields,
    levels,
    isDirty,
    validationErrors,
    isSaveValid,
    load,
    save,
    syncSortOrders,
    setFieldVisible,
    setFieldRequired,
    setLevelAlias,
    hydrateFromPayload,
  };
});
